import { api } from '@/api/client'

export type PollStatus = 'draft' | 'published' | 'withdrawn'
export type PollTargetRole = 'member' | 'trainer'

export type PollOption = {
  id: string
  label: string
  position: number
  added_by_participant: boolean
  // Null until the results are released to the current user.
  vote_count: number | null
}

export type MyPoll = {
  id: string
  title: string
  body: string
  allow_multiple: boolean
  allow_participant_options: boolean
  results_visible: boolean
  visible_from: string | null
  visible_until: string | null
  published_at: string | null
  is_open: boolean
  has_voted: boolean
  dismissed: boolean
  my_option_ids: string[]
  voter_count: number | null
  options: PollOption[]
}

export type AdminPoll = {
  id: string
  title: string
  body: string
  target_roles: PollTargetRole[]
  allow_multiple: boolean
  allow_participant_options: boolean
  results_visible: boolean
  status: PollStatus
  visible_from: string | null
  visible_until: string | null
  published_at: string | null
  withdrawn_at: string | null
  created_by: string | null
  created_at: string
  updated_at: string
  options: AdminPollOption[]
  option_count: number
  voter_count: number
  target_count: number
}

export type AdminPollOption = {
  id: string
  label: string
  added_by_participant: boolean
  author_name: string | null
  vote_count: number
}

export type PollResultOption = {
  option_id: string
  option_label: string
  option_position: number
  added_by_participant: boolean
  author_name: string | null
  vote_count: number
}

export type PollDraft = {
  title: string
  body: string
  target_roles: PollTargetRole[]
  allow_multiple: boolean
  allow_participant_options: boolean
  results_visible: boolean
  visible_from: string | null
  visible_until: string | null
  // Full ordered list: existing options keep their id (and votes), new ones have none.
  options: { id: string | null; label: string }[]
}

export type PollVote = {
  optionIds: string[]
  newOptions: string[]
}

export async function getMyPolls(): Promise<MyPoll[]> {
  const { data } = await api.GET('/api/v1/users/me/polls')
  return data! as MyPoll[]
}

export async function votePoll(id: string, vote: PollVote): Promise<void> {
  await api.PUT('/api/v1/polls/{id}/vote', {
    params: { path: { id } },
    body: { option_ids: vote.optionIds, new_options: vote.newOptions },
  })
}

export async function dismissPoll(id: string): Promise<void> {
  await api.POST('/api/v1/polls/{id}/dismissal', { params: { path: { id } } })
}

export async function adminListPolls(): Promise<AdminPoll[]> {
  const { data } = await api.GET('/api/v1/polls')
  return data! as AdminPoll[]
}

export async function adminGetPollResults(id: string): Promise<PollResultOption[]> {
  const { data } = await api.GET('/api/v1/polls/{id}/results', { params: { path: { id } } })
  return data!
}

export async function savePoll(id: string | null, draft: PollDraft): Promise<string> {
  const body = {
    title: draft.title,
    body: draft.body,
    target_roles: draft.target_roles,
    allow_multiple: draft.allow_multiple,
    allow_participant_options: draft.allow_participant_options,
    results_visible: draft.results_visible,
    visible_from: draft.visible_from,
    visible_until: draft.visible_until,
    options: draft.options,
  }
  const { data } = id
    ? await api.PUT('/api/v1/polls/{id}', { params: { path: { id } }, body })
    : await api.POST('/api/v1/polls', { body })
  return data!.id
}

export async function deletePoll(id: string): Promise<void> {
  await api.DELETE('/api/v1/polls/{id}', { params: { path: { id } } })
}

export async function publishPoll(id: string): Promise<void> {
  await api.POST('/api/v1/polls/{id}/publication', { params: { path: { id } } })
}

export async function withdrawPoll(id: string): Promise<void> {
  await api.POST('/api/v1/polls/{id}/withdrawal', { params: { path: { id } } })
}

export async function closePoll(id: string): Promise<void> {
  await api.POST('/api/v1/polls/{id}/closing', { params: { path: { id } } })
}
